import { useEffect, useRef, useState } from "react";

/**
 * Reports whether the referenced element has been on screen at least once.
 *
 * Once it has, it stays true and the observer is dropped: this answers "has the
 * learner had a chance to see this yet", which is a thing that happens once, not
 * a live visibility flag that flips back on every scroll.
 *
 * `threshold` is the share of the element that has to be visible to count - a
 * fraction peeking over the fold is not yet a chance to see it.
 */
export const useInViewOnce = <T extends HTMLElement>(threshold = 0.25) => {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current || inView) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setInView(true);
        // Seen for good - stop paying for scroll events on this element.
        observer.disconnect();
      },
      { threshold }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [inView, threshold]);

  return { ref, inView };
};
